import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { ArrowUpRight, LogIn, ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/data/page-header'
import { RoleBadge } from '@/components/data/badges'
import { FilterSelect, PAGE_SIZE, Pagination, SearchBox, SortHead, ToggleChip } from '@/components/data/list-controls'
import { MonoId } from '@/components/data/mono-id'
import { Person } from '@/components/data/person'
import { EmptyState, ErrorState, SkeletonRows, Warnings } from '@/components/data/states'
import { useListParams } from '@/hooks/use-list-params'
import { errorMessage, impersonate } from '@/lib/api'
import { formatDate, formatDateTime, formatRelative } from '@/lib/format'
import { providerLabels } from '@/lib/labels'
import { useUsers } from '@/lib/queries'
import type { UserItem } from '@/lib/types'

const defaults = { sort: 'created_at', order: 'desc' }
const FILTERS = ['verified', 'provider', 'role', 'no_team', 'disabled', 'search']

function TeamChips({ user, max = 2 }: { user: UserItem; max?: number }) {
  if (user.teams.length === 0) return <span className="text-warn">No team</span>
  return (
    <div className="flex flex-wrap items-center gap-1">
      {user.teams.slice(0, max).map((t) => (
        <Link
          key={t.id}
          to={`/teams/${t.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-5 max-w-40 items-center gap-1 rounded-full bg-secondary px-2 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate">{t.name || `Team ${t.id}`}</span>
          {t.role === 'admin' && <ShieldCheck className="size-3 shrink-0 text-primary" aria-label="admin" />}
        </Link>
      ))}
      {user.teams.length > max && <span className="text-xs text-muted-foreground">+{user.teams.length - max}</span>}
    </div>
  )
}

function UserDrawer({ user, onClose }: { user: UserItem | undefined; onClose: () => void }) {
  const [busy, setBusy] = useState(false)
  const signInAs = async (u: UserItem) => {
    setBusy(true)
    try {
      await impersonate(u.uid)
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet open={!!user} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="gap-0 sm:max-w-md">
        {user && (
          <>
            <SheetHeader className="border-b pr-12">
              <SheetTitle className="text-lg">{user.name || user.email || 'Unknown account'}</SheetTitle>
              <SheetDescription>{user.email}</SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-5 overflow-y-auto p-4">
              <div className="flex flex-wrap gap-2">
                {user.email_verified ? <Badge variant="secondary">Email verified</Badge> : <Badge variant="outline">Email not verified</Badge>}
                {user.disabled && <Badge variant="destructive">Disabled</Badge>}
                {user.providers.map((p) => (
                  <Badge key={p} variant="outline">
                    {providerLabels[p] ?? p}
                  </Badge>
                ))}
              </div>
              <dl className="divide-y">
                <div className="flex justify-between gap-4 py-2.5">
                  <dt className="text-muted-foreground">Joined</dt>
                  <dd>{formatDateTime(user.created_at)}</dd>
                </div>
                <div className="flex justify-between gap-4 py-2.5">
                  <dt className="text-muted-foreground">Last sign-in</dt>
                  <dd>{user.last_sign_in_at ? `${formatDateTime(user.last_sign_in_at)} (${formatRelative(user.last_sign_in_at)})` : 'Never'}</dd>
                </div>
                <div className="flex justify-between gap-4 py-2.5">
                  <dt className="text-muted-foreground">Firebase uid</dt>
                  <dd className="min-w-0">
                    <MonoId value={user.uid} />
                  </dd>
                </div>
              </dl>
              <Button variant="outline" className="w-full" disabled={busy || user.disabled} onClick={() => signInAs(user)}>
                <LogIn /> {user.disabled ? 'Account disabled' : 'Sign in as this user'}
              </Button>
              <section>
                <h3 className="mb-2 font-semibold">Teams</h3>
                {user.teams.length === 0 ? (
                  <p className="text-muted-foreground">This person doesn’t belong to any team.</p>
                ) : (
                  <ul className="divide-y rounded-lg border">
                    {user.teams.map((t) => (
                      <li key={t.id}>
                        <Link to={`/teams/${t.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-accent/40">
                          <span className="truncate font-medium">{t.name || `Team ${t.id}`}</span>
                          <span className="flex shrink-0 items-center gap-2">
                            <RoleBadge role={t.role} />
                            <ArrowUpRight className="size-3.5 text-muted-foreground" />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default function UsersPage() {
  const { params, set } = useListParams(defaults)
  const { data, error, isPending, isFetching, refetch } = useUsers(params)
  const offset = Number(params.offset ?? 0)
  const open = data?.items.find((u) => u.uid === params.user)
  const filtered = FILTERS.some((k) => params[k])
  const clear = () => set(Object.fromEntries(FILTERS.map((k) => [k, ''])))
  const sortProps = { sort: params.sort, order: params.order, onSort: (sort: string, order: string) => set({ sort, order }) }

  return (
    <>
      <PageHeader title="Users">
        {data ? <>{data.total.toLocaleString('en-US')} {filtered ? 'matching' : 'accounts'}</> : 'Everyone who has signed up.'}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox value={params.search ?? ''} onChange={(search) => set({ search })} placeholder="Name, email, uid or team" />
        <FilterSelect
          label="Email"
          value={params.verified ?? ''}
          onChange={(verified) => set({ verified })}
          options={[{ value: 'true', label: 'Verified' }, { value: 'false', label: 'Not verified' }]}
        />
        <FilterSelect
          label="Sign-in"
          value={params.provider ?? ''}
          onChange={(provider) => set({ provider })}
          options={Object.entries(providerLabels).map(([value, label]) => ({ value, label }))}
        />
        <FilterSelect label="Role" value={params.role ?? ''} onChange={(role) => set({ role })} options={[{ value: 'admin', label: 'Team admin' }, { value: 'user', label: 'Member' }]} />
        <ToggleChip pressed={params.no_team === '1'} onChange={(on) => set({ no_team: on ? '1' : '' })}>
          No team
        </ToggleChip>
        <ToggleChip pressed={params.disabled === '1'} onChange={(on) => set({ disabled: on ? '1' : '' })}>
          Disabled
        </ToggleChip>
        {filtered && (
          <Button variant="ghost" size="sm" onClick={clear}>
            Clear filters
          </Button>
        )}
      </div>

      <Warnings warnings={data?.warnings} />

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortHead id="email" {...sortProps}>
                  Person
                </SortHead>
                <SortHead id="teams" {...sortProps}>
                  Teams
                </SortHead>
                <TableHead>Email</TableHead>
                <TableHead>Sign-in</TableHead>
                <SortHead id="created_at" {...sortProps}>
                  Joined
                </SortHead>
                <SortHead id="last_sign_in" {...sortProps}>
                  Last sign-in
                </SortHead>
              </TableRow>
            </TableHeader>
            <TableBody className={isFetching && !isPending ? 'opacity-70' : undefined}>
              {isPending ? (
                <SkeletonRows columns={6} />
              ) : (
                data?.items.map((u) => (
                  <TableRow key={u.uid} tabIndex={0} className="cursor-pointer" onClick={() => set({ user: u.uid })} onKeyDown={(e) => e.key === 'Enter' && set({ user: u.uid })}>
                    <TableCell className="max-w-64">
                      <Person name={u.name} email={u.email} uid={u.uid} />
                    </TableCell>
                    <TableCell>
                      <TeamChips user={u} />
                    </TableCell>
                    <TableCell>
                      {u.disabled ? <Badge variant="destructive">Disabled</Badge> : u.email_verified ? 'Verified' : <span className="text-warn">Not verified</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.providers.map((p) => providerLabels[p] ?? p).join(', ') || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatRelative(u.last_sign_in_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {error && <ErrorState error={error} onRetry={() => refetch()} />}
        {!isPending && !error && data?.items.length === 0 && (
          <EmptyState title={filtered ? 'No account matches these filters' : 'No accounts yet'}>{filtered ? 'Widen the search or remove a filter.' : 'Accounts appear once people sign up.'}</EmptyState>
        )}
        <Pagination total={data?.total ?? 0} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => set({ offset: String(o) })} />
      </div>

      <UserDrawer user={open} onClose={() => set({ user: '' })} />
    </>
  )
}
